import { useEffect, useState } from 'react';
import {
  DEFAULT_AUTO_WORKFLOW_PLAN,
  normalizeAutoWorkflowPlan,
  type AutoWorkflowPlan,
} from '@sr-test-wizard/shared';
import { runVideosApi } from '../../lib/runVideosApi';
import { useAutoWorkflowStore } from '../../state/autoWorkflowStore';
import { useVideosStore } from '../../state/videosStore';

interface Props {
  runId: string;
  gateOk: boolean;
  gateReason: string | null;
  onClose: () => void;
}

function anyStep(plan: AutoWorkflowPlan): boolean {
  return (
    plan.populateTexec ||
    plan.envLinks ||
    plan.pullSource ||
    plan.images ||
    plan.video ||
    plan.gameKb ||
    plan.texec
  );
}

/**
 * Picker for the left-rail Auto workflow. Nested options for Game Knowledge
 * and TEXEC; optional video upload lands in the Phase 5 videos list.
 */
export function AutoWorkflowDialog({ runId, gateOk, gateReason, onClose }: Props) {
  const status = useAutoWorkflowStore((s) => s.status);
  const start = useAutoWorkflowStore((s) => s.start);
  const stop = useAutoWorkflowStore((s) => s.stop);
  const loadVideos = useVideosStore((s) => s.load);
  const videoList = useVideosStore((s) => s.list);
  const videosRunId = useVideosStore((s) => s.runId);

  const [plan, setPlan] = useState<AutoWorkflowPlan>(DEFAULT_AUTO_WORKFLOW_PLAN);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);

  const running = status?.running === true;
  const normalized = normalizeAutoWorkflowPlan(plan);
  const showDisclaimer = !normalized.video || !normalized.gameKb;
  const videosOnThisRun = videosRunId === runId ? videoList : [];

  useEffect(() => {
    void loadVideos(runId);
  }, [runId, loadVideos]);

  const patch = (partial: Partial<AutoWorkflowPlan>) => {
    setPlan((prev) => normalizeAutoWorkflowPlan({ ...prev, ...partial }));
  };

  const onStart = async () => {
    if (!gateOk || running || !anyStep(normalized)) return;
    setBusy(true);
    setError(null);
    try {
      await start(runId, normalized);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onStop = async () => {
    setBusy(true);
    setError(null);
    try {
      await stop(runId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (file: File | null) => {
    if (!file) return;
    setError(null);
    setUploadPct(0);
    try {
      await runVideosApi.upload(runId, file, (pct) => setUploadPct(pct));
      await loadVideos(runId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploadPct(null);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal auto-wf-modal" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>Auto workflow</h2>
          <span className="spacer" />
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
        <p className="muted small" style={{ marginTop: 0 }}>
          Runs the selected phases in order. Source pull and image download can overlap.
          Game Knowledge and TEXEC wait for earlier selected work. Existing artifacts are
          skipped; TEXEC data and env links always refresh when selected.
        </p>

        {!gateOk && <p className="warn">{gateReason}</p>}
        {error && <p className="bad">{error}</p>}
        {running && (
          <p className="muted small">
            Running: {status?.message ?? status?.step ?? 'in progress'}
          </p>
        )}

        <fieldset className="auto-wf-list" disabled={running}>
          <label className="auto-wf-step">
            <input
              type="checkbox"
              checked={normalized.populateTexec}
              onChange={(e) => patch({ populateTexec: e.target.checked })}
            />
            <span>
              <strong>1. Populate TEXEC data</strong>
              <span className="muted small">Refresh from Jira</span>
            </span>
          </label>
          <label className="auto-wf-step">
            <input
              type="checkbox"
              checked={normalized.envLinks}
              onChange={(e) => patch({ envLinks: e.target.checked })}
            />
            <span>
              <strong>2. Environment links</strong>
              <span className="muted small">Regenerate market URLs</span>
            </span>
          </label>
          <label className="auto-wf-step">
            <input
              type="checkbox"
              checked={normalized.pullSource}
              disabled={normalized.texec || normalized.gameKb}
              onChange={(e) => patch({ pullSource: e.target.checked })}
            />
            <span>
              <strong>3. Pull source</strong>
              <span className="muted small">Pull only — no AI nominate</span>
            </span>
          </label>
          <label className="auto-wf-step">
            <input
              type="checkbox"
              checked={normalized.images}
              disabled={normalized.texec}
              onChange={(e) => patch({ images: e.target.checked })}
            />
            <span>
              <strong>4. Images</strong>
              <span className="muted small">Download, then analyze</span>
            </span>
          </label>
          <label className="auto-wf-step">
            <input
              type="checkbox"
              checked={normalized.video}
              onChange={(e) => patch({ video: e.target.checked })}
            />
            <span>
              <strong>5. Gameplay video</strong>
              <span className="muted small">Drive gameplay folder, or upload below</span>
            </span>
          </label>
          {normalized.video && (
            <div className="auto-wf-nested">
              {videosOnThisRun.length > 0 ? (
                <span className="small good">
                  {videosOnThisRun.length} video{videosOnThisRun.length === 1 ? '' : 's'} already
                  in Phase 5
                </span>
              ) : (
                <span className="muted small">
                  No video on disk yet — will import from Drive, or upload now.
                </span>
              )}
              <label className="small">
                Upload video
                <input
                  type="file"
                  accept="video/*,.mp4,.mov,.webm,.mkv,.m4v"
                  onChange={(e) => void onUpload(e.target.files?.[0] ?? null)}
                />
              </label>
              {uploadPct != null && <span className="muted small">Uploading {uploadPct}%</span>}
            </div>
          )}
          <label className="auto-wf-step">
            <input
              type="checkbox"
              checked={normalized.gameKb}
              onChange={(e) =>
                patch({
                  gameKb: e.target.checked,
                  gameKbDescription: e.target.checked,
                  gameKbReport: e.target.checked,
                })
              }
            />
            <span>
              <strong>6. Game Knowledge</strong>
              <span className="muted small">Waits for source, images, and video if selected</span>
            </span>
          </label>
          {normalized.gameKb && (
            <div className="auto-wf-nested">
              <label>
                <input
                  type="checkbox"
                  checked={normalized.gameKbDescription}
                  onChange={(e) => patch({ gameKbDescription: e.target.checked })}
                />
                Game Description
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={normalized.gameKbReport}
                  onChange={(e) => patch({ gameKbReport: e.target.checked })}
                />
                Full Report
              </label>
            </div>
          )}
          <label className="auto-wf-step">
            <input
              type="checkbox"
              checked={normalized.texec}
              onChange={(e) => patch({ texec: e.target.checked })}
            />
            <span>
              <strong>7. TEXEC execution</strong>
              <span className="muted small">Waits for source + image analysis</span>
            </span>
          </label>
          {normalized.texec && (
            <div className="auto-wf-nested" role="radiogroup" aria-label="TEXEC mode">
              <label>
                <input
                  type="radio"
                  name="texecMode"
                  checked={normalized.texecMode === 'all'}
                  onChange={() => patch({ texecMode: 'all' })}
                />
                All tests
              </label>
              <label>
                <input
                  type="radio"
                  name="texecMode"
                  checked={normalized.texecMode === 'tba'}
                  onChange={() => patch({ texecMode: 'tba' })}
                />
                All still TBA / unranked
              </label>
            </div>
          )}
        </fieldset>

        {showDisclaimer && (
          <p className="auto-wf-disclaimer">
            Video analysis and Game Knowledge strongly improve TEXEC results. You can continue
            without them, but verdicts will be weaker.
          </p>
        )}

        <div className="row" style={{ marginTop: 16, gap: 8 }}>
          {running ? (
            <button type="button" className="btn" disabled={busy} onClick={() => void onStop()}>
              {busy ? 'Stopping…' : 'Stop'}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || !gateOk || !anyStep(normalized)}
              onClick={() => void onStart()}
            >
              {busy ? 'Starting…' : 'Start'}
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
